/**
 * DeliveryQualityDialog — bám 1:1 Access form `frmFactoryQryDeliveryAddQC`
 * (`plan/access_all/form_frmFactoryQryDeliveryAddQC.txt`). Mirror cấu trúc
 * FactoryReceiveQualityDialog: header delivery read-only + defect grid editable.
 *
 * Khác Receive QC: form Delivery QC KHÔNG có cột Real/Standard/Deduction —
 * mỗi defect chỉ 1 ô giá trị (single value). Không lookup quality references.
 *
 * Header block (trên "Quality's Information") theo Access Form_Load
 * `Set_Enabled` (module_modfactoryControl — CHỈ đụng acTextBox):
 *   - TextBox KHÔNG trong list quality → DISABLED: gdn_id, date_out,
 *     from_warehouse, to_warehouse, lot_name_buyer, number_theory_of_bag,
 *     real_bags, truck_no, shipments_weight, gross_weight, bags_on_truck,
 *     net_real_weight, note.
 *   - ComboBox không bị Set_Enabled đụng → giữ design-time ENABLED: do_no,
 *     lot_id, stack_id, package, package_to_truck (+ gdn_id_copy).
 *   - to_warehouse: disabled khi mở; `do_no_AfterUpdate` user đổi DO → non-MOV
 *     enable, MOV auto-fill từ moving order + disable.
 * Save persist các field ENABLED vào bảng `delivery` (yêu cầu chị Đẹp).
 *
 * Required (Access `strCheck = ",gdn_id,stack_id,do_no,date_out,"`).
 *
 * Save payload (Access `strU_mainsub`/`strI_mainsub`):
 *   spongy_beans, old_crop, mc, blacks, brokens, black_and_broken, fm, mouldy,
 *   brown, partly_black, bleach, immature, broken_over_1_per_2,
 *   pulper_nipped_beans, blotchy_beans, waxy_beans, insect_beans,
 *   triages_defects, fb, overdry, cherry, yellowbeans, polish_degree, s20, s19,
 *   s18, s16, s15, s14, s13, s12, under_s12, total_defect, cuptest,
 *   deduction_rate, qc_name, supervisor, note.
 *
 * Auto-sums (Access AfterUpdate):
 *   black_and_broken = blacks + brokens                  (Sub BlackBroken)
 *   triages_defects  = spongy_beans + black_and_broken + fm + mouldy + brown +
 *                      bleach + partly_black + immature + broken_over_1_per_2 +
 *                      pulper_nipped_beans + blotchy_beans + waxy_beans +
 *                      insect_beans + fb + overdry + yellowbeans
 *                                                         (Sub TriagesDefects)
 *   (total_defect KHÔNG auto-compute trong Access — editable.)
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'

import type { FactoryCell } from '@/store/api/factoryApi'
import {
  useGetDeliveryCombosQuery,
  useLazyGetDeliveryLookupsQuery,
  useLazyGetDeliveryQualityQuery,
} from '@/store/api/factoryApi'
import { TEXT_TRUNCATE } from '@/theme/responsive'

/** Fields copied by Access `changeGDN` khi chọn gdn_id_copy (34 defect/size,
 *  KHÔNG gồm deduction_rate/qc_name/supervisor). */
const COPY_FIELDS = [
  'mc', 'blacks', 'brokens', 'black_and_broken', 'fm', 'mouldy', 'brown',
  'partly_black', 'bleach', 'spongy_beans', 'immature', 'old_crop',
  'broken_over_1_per_2', 'pulper_nipped_beans', 'blotchy_beans', 'waxy_beans',
  'insect_beans', 'triages_defects', 'fb', 'overdry', 'cherry', 'yellowbeans',
  'polish_degree', 's20', 's19', 's18', 's16', 's15', 's14', 's13', 's12',
  'under_s12', 'total_defect', 'cuptest',
]

export type DeliveryQualityDialogMode = 'new' | 'edit' | 'view'

export interface DeliveryQualityDialogProps {
  open: boolean
  mode: DeliveryQualityDialogMode
  factoryCode: string
  initialData: Record<string, FactoryCell> | null
  saving: boolean
  onClose: () => void
  onSave: (payload: Record<string, FactoryCell | undefined>) => void
}

/** Defect/quality fields editable per Access Set_Enabled. */
const EDITABLE = new Set([
  'spongy_beans', 'old_crop', 'mc', 'blacks', 'brokens', 'fm', 'mouldy',
  'brown', 'partly_black', 'bleach', 'immature', 'broken_over_1_per_2',
  'pulper_nipped_beans', 'blotchy_beans', 'waxy_beans', 'insect_beans',
  'fb', 'overdry', 'cherry', 'yellowbeans', 'polish_degree',
  's20', 's19', 's18', 's16', 's15', 's14', 's13', 's12', 'under_s12',
  'total_defect', 'cuptest', 'deduction_rate', 'qc_name', 'supervisor',
])

/** Bean fields contribute to triages_defects (Access Sub TriagesDefects). */
const TRIAGES_INPUTS = [
  'spongy_beans', 'black_and_broken', 'fm', 'mouldy', 'brown', 'bleach',
  'partly_black', 'immature', 'broken_over_1_per_2', 'pulper_nipped_beans',
  'blotchy_beans', 'waxy_beans', 'insect_beans', 'fb', 'overdry', 'yellowbeans',
]

const COMPUTED_FIELDS = new Set(['black_and_broken', 'triages_defects'])

/** String fields — không cast number trong payload. */
const STRING_FIELDS = new Set([
  'gdn_id', 'date_out', 'do_no', 'from_warehouse', 'to_warehouse',
  'lot_name_buyer', 'lot_id', 'stack_id', 'package', 'package_to_truck',
  'truck_no', 'note', 'qc_name', 'supervisor', 'cuptest',
])

/**
 * Delivery header (Enabled/Disabled theo Access `Set_Enabled` — xem docstring
 * đầu file) — bám Access từng HÀNG theo Top (col A Left 3180, col B Left
 * 9624). `do_no` đứng riêng 1 hàng (Access T420, col B trống) → phải align
 * theo hàng, KHÔNG render 2 cột độc lập (sẽ lệch col B từ hàng 2). `note`
 * full-width riêng phía dưới.
 */
type HeaderCell = { field: string; label: string }
const HEADER_ROWS: Array<[HeaderCell | null, HeaderCell | null]> = [
  [{ field: 'gdn_id', label: 'GDN ID' }, { field: 'date_out', label: 'Date Out' }],
  [{ field: 'do_no', label: 'DO No' }, null],
  [{ field: 'from_warehouse', label: 'From Warehouse' }, { field: 'to_warehouse', label: 'To Warehouse' }],
  [{ field: 'lot_id', label: 'Lot ID' }, { field: 'lot_name_buyer', label: 'Lot Name Buyer' }],
  [{ field: 'stack_id', label: 'Stack ID' }, { field: 'package', label: 'Package' }],
  [{ field: 'number_theory_of_bag', label: 'Number Theory Of Bag' }, { field: 'real_bags', label: 'Real Bags' }],
  [{ field: 'truck_no', label: 'Truck No' }, { field: 'package_to_truck', label: 'Package To Truck' }],
  [{ field: 'shipments_weight', label: 'Shipments Weight' }, { field: 'bags_on_truck', label: 'Bags On Truck' }],
  [{ field: 'gross_weight', label: 'Gross Weight' }, { field: 'net_real_weight', label: 'Net Real Weight' }],
]

/**
 * Quality defect grid — 3 columns bám Access Left 2352 / 7032 / 11040, theo Top.
 * deduction_rate / qc_name / supervisor nằm ở ĐÁY (Access Top 7680+), render riêng.
 */
const QUALITY_COL_1 = [
  'mc', 'blacks', 'brokens', 'black_and_broken', 'fm', 'mouldy', 'brown',
  'partly_black', 'bleach', 'spongy_beans', 'immature', 'old_crop',
]
const QUALITY_COL_2 = [
  'broken_over_1_per_2', 'pulper_nipped_beans', 'blotchy_beans', 'waxy_beans',
  'insect_beans', 'triages_defects', 'fb', 'overdry', 'cherry', 'yellowbeans',
]
const QUALITY_COL_3 = [
  'polish_degree', 's20', 's19', 's18', 's16', 's15', 's14', 's13', 's12',
  'under_s12', 'total_defect', 'cuptest',
]
const QUALITY_COLUMNS = [QUALITY_COL_1, QUALITY_COL_2, QUALITY_COL_3]

const FIELD_LABELS: Record<string, string> = {
  mc: 'MC',
  blacks: 'Blacks',
  brokens: 'Brokens',
  black_and_broken: 'Black & Broken',
  fm: 'FM',
  mouldy: 'Mouldy',
  brown: 'Brown',
  partly_black: 'Partly Black',
  bleach: 'Bleach',
  spongy_beans: 'Spongy Beans',
  immature: 'Immature',
  old_crop: 'Previous Crop',
  broken_over_1_per_2: 'Broken > 1/2',
  pulper_nipped_beans: 'Pulper Nipped',
  blotchy_beans: 'Blotchy Beans',
  waxy_beans: 'Waxy Beans',
  insect_beans: 'Insect Beans',
  triages_defects: 'Triages Defects',
  fb: 'FB',
  overdry: 'Overdry',
  cherry: 'Cherry',
  yellowbeans: 'Yellow Beans',
  polish_degree: 'Polish Degree',
  s20: 'S20',
  s19: 'S19',
  s18: 'S18',
  s16: 'S16',
  s15: 'S15',
  s14: 'S14',
  s13: 'S13',
  s12: 'S12',
  under_s12: '< S12',
  total_defect: 'Total Defect',
  cuptest: 'Cup Test',
  deduction_rate: 'Deduction Rate',
}

const NUMBER_DISPLAY_FIELDS = new Set([
  'number_theory_of_bag', 'real_bags', 'shipments_weight', 'bags_on_truck',
  'gross_weight', 'net_real_weight',
])
const DATE_DISPLAY_FIELDS = new Set(['date_out'])

/** package / package_to_truck — Access `RowSource = "BB;PP;JB;BULK"`. */
const PACKAGE_ENUM = ['BB', 'PP', 'JB', 'BULK']

/** Header fields ENABLED (Access Set_Enabled không đụng ComboBox) — persist
 *  khi Save vào bảng delivery. to_warehouse chỉ gửi khi user mở khoá qua
 *  do_no_AfterUpdate. gdn_id KHÔNG gửi — là key WHERE của PATCH. */
const HEADER_SAVE_FIELDS = [
  'do_no', 'lot_id', 'stack_id', 'package', 'package_to_truck', 'to_warehouse',
]

/** TextBox header DISABLED theo Access `Set_Enabled` (chỉ enable acTextBox
 *  trong list quality; TextBox khác → Enabled=False). to_warehouse xử lý
 *  riêng (cascade do_no_AfterUpdate). */
const HEADER_READONLY_FIELDS = new Set([
  'gdn_id', 'date_out', 'from_warehouse', 'lot_name_buyer',
  'number_theory_of_bag', 'real_bags', 'truck_no', 'shipments_weight',
  'gross_weight', 'bags_on_truck', 'net_real_weight',
])

/** Required theo Access `strCheck = ",gdn_id,stack_id,do_no,date_out,"`. */
const HEADER_REQUIRED_FIELDS = ['gdn_id', 'stack_id', 'do_no', 'date_out']

interface ComboItem {
  value: string
  label: string
}

/** Flat option label "<CODE> — <NAME>"; collapse khi code === label. */
function comboLabel(value: string, label: string | null | undefined): string {
  const l = (label ?? '').trim()
  if (!l || l === value) return value
  return `${value} — ${l}`
}

const ACCESS_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

function stringifyCell(v: FactoryCell | undefined): string {
  if (v == null) return ''
  return typeof v === 'string' ? v : String(v)
}

function numOrZero(s: string): number {
  const n = Number((s ?? '').toString().replace(/,/g, '').trim())
  return Number.isFinite(n) ? n : 0
}

/** Standard format `#,##0.00` (Access Format "#,#00…" Decimals=2) — display +
 *  blur reformat (skill /convert-form §Number). Blank stays blank. */
function formatStandard(v: string | number | null | undefined): string {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return ''
  const n = Number(s)
  if (!Number.isFinite(n)) return String(v ?? '')
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Header weights = Access Format Standard (2 decimals); counts = General int. */
const STANDARD_HEADER_FIELDS = new Set(['shipments_weight', 'gross_weight', 'net_real_weight'])

function toPayloadValue(field: string, raw: string): FactoryCell | undefined {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  if (STRING_FIELDS.has(field)) return raw
  const n = Number(trimmed.replace(/,/g, '')) // strip Standard thousand separators
  if (Number.isFinite(n)) return n
  return raw
}

function formatDateDisplay(value: string): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  return `${day}-${month}-${d.getFullYear()}`
}

function formatNumberDisplay(value: string): string {
  if (!value) return ''
  const n = Number(value)
  if (!Number.isFinite(n)) return value
  return n.toLocaleString('en-US', {
    maximumFractionDigits: Number.isInteger(n) ? 0 : 4,
  })
}

function headerDisplayValue(field: string, raw: string): string {
  if (DATE_DISPLAY_FIELDS.has(field)) return formatDateDisplay(raw)
  if (STANDARD_HEADER_FIELDS.has(field)) return formatStandard(raw)
  if (NUMBER_DISPLAY_FIELDS.has(field)) return formatNumberDisplay(raw)
  return raw
}

const sectionHeaderSx: SxProps<Theme> = (theme) => ({
  px: 1.25,
  py: 0.5,
  bgcolor: alpha(theme.palette.primary.main, 0.08),
  borderLeft: 3,
  borderColor: 'primary.main',
  borderRadius: 0.5,
  mt: 1.5,
  mb: 1,
})

const compactSx = {
  '& .MuiInputBase-root': { minHeight: 30, fontSize: '0.78rem' },
  '& .MuiInputBase-input': { py: 0.4, fontSize: '0.78rem' },
  '& .MuiInputLabel-root': { fontSize: '0.78rem', fontWeight: 500 },
}

const gridInputSx = {
  '& .MuiInputBase-root': { minHeight: 28, fontSize: '0.75rem' },
  '& .MuiInputBase-input': { py: 0.3, fontSize: '0.75rem' },
}

const titleBtnSx = {
  py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem', lineHeight: 1.2,
  textTransform: 'none' as const,
}

const labelCellSx: SxProps<Theme> = (theme) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  px: 0.5,
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.primary,
})

export default function DeliveryQualityDialog({
  open,
  mode,
  factoryCode,
  initialData,
  saving,
  onClose,
  onSave,
}: DeliveryQualityDialogProps) {
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [copyError, setCopyError] = useState('')
  // Access: to_warehouse Enabled chỉ sau do_no_AfterUpdate non-MOV.
  const [toWarehouseEnabled, setToWarehouseEnabled] = useState(false)

  const isView = mode === 'view'

  // Combo sources (Access Form_Load RowSource): do_no / lot_id / stack_id cho
  // header editable + gdn_copy cho combo copy quality. Chỉ cần khi nhập.
  const combosQuery = useGetDeliveryCombosQuery({ factoryCode }, { skip: !open || isView })
  const gdnCopyOptions = combosQuery.data?.gdn_copy ?? []
  const doOptions = combosQuery.data?.do_no ?? []
  const lotOptions = combosQuery.data?.lot_id ?? []
  const stackOptions = combosQuery.data?.stack_id ?? []
  const [fetchQuality, { isFetching: copyLoading }] = useLazyGetDeliveryQualityQuery()
  const [triggerLookups] = useLazyGetDeliveryLookupsQuery()

  /** Hydrate draft khi mở dialog hoặc đổi initialData. */
  useEffect(() => {
    if (!open) return
    setCopyError('')
    const next: Record<string, string> = {}
    if (initialData) {
      for (const k of Object.keys(initialData)) next[k] = stringifyCell(initialData[k])
    }
    // Pre-format defect/quality numeric fields → Standard (skill §Number hydrate).
    for (const k of Object.keys(next)) {
      if ((EDITABLE.has(k) || COMPUTED_FIELDS.has(k)) && !STRING_FIELDS.has(k)) {
        next[k] = formatStandard(next[k])
      }
    }
    setDraft(next)
    // Access: to_warehouse disabled khi mở form; chỉ do_no_AfterUpdate (user
    // đổi DO) mới enable — AfterUpdate KHÔNG chạy lúc load record.
    setToWarehouseEnabled(false)
  }, [open, mode, initialData])

  // Selected + inject fallback vào options (skill /convert-form anti-pattern
  // #46) — value đã lưu phải hiện chip dù vắng trong list (limit/filter).
  const selectedDo = useMemo<ComboItem | null>(() => {
    const v = (draft['do_no'] ?? '').trim()
    if (!v) return null
    const found = doOptions.find((o) => o.value === v)
    return found ? { value: found.value, label: found.label } : { value: v, label: v }
  }, [draft, doOptions])
  const selectedLot = useMemo<ComboItem | null>(() => {
    const v = (draft['lot_id'] ?? '').trim()
    if (!v) return null
    const found = lotOptions.find((o) => o.value === v)
    return found ? { value: found.value, label: found.label } : { value: v, label: v }
  }, [draft, lotOptions])
  const selectedStack = useMemo<ComboItem | null>(() => {
    const v = (draft['stack_id'] ?? '').trim()
    if (!v) return null
    const found = stackOptions.find((o) => o.value === v)
    return found ? { value: found.value, label: found.label } : { value: v, label: v }
  }, [draft, stackOptions])
  const doItems = useMemo<ComboItem[]>(() => {
    const base = doOptions.map((o) => ({ value: o.value, label: o.label }))
    return selectedDo && !base.some((o) => o.value === selectedDo.value)
      ? [selectedDo, ...base] : base
  }, [doOptions, selectedDo])
  const lotItems = useMemo<ComboItem[]>(() => {
    const base = lotOptions.map((o) => ({ value: o.value, label: o.label }))
    return selectedLot && !base.some((o) => o.value === selectedLot.value)
      ? [selectedLot, ...base] : base
  }, [lotOptions, selectedLot])
  const stackItems = useMemo<ComboItem[]>(() => {
    const base = stackOptions.map((o) => ({ value: o.value, label: o.label }))
    return selectedStack && !base.some((o) => o.value === selectedStack.value)
      ? [selectedStack, ...base] : base
  }, [stackOptions, selectedStack])

  // Access `do_no_AfterUpdate` (chạy khi USER đổi DO, không chạy lúc load):
  // MOV → to_warehouse từ moving order + Enabled=False; non-MOV → Enabled=True.
  const handleDoNoChange = (value: string) => {
    setDraft((prev) => ({ ...prev, do_no: value }))
    const dn = value.trim()
    if (!dn) return
    if (dn.startsWith('MOV')) {
      setToWarehouseEnabled(false)
      void triggerLookups({ factoryCode, doNo: dn })
        .unwrap()
        .then((res) => {
          if (res.is_move && res.to_warehouse != null) {
            setDraft((prev) => ({ ...prev, to_warehouse: res.to_warehouse ?? '' }))
          }
        })
        .catch(() => undefined)
    } else {
      setToWarehouseEnabled(true)
    }
  }

  /** Access `changeGDN`: chọn gdn_id_copy → copy 34 field quality từ GDN đó.
   *  Mirror combo "Copy Quality from another GRN" (FactoryReceiveQualityDialog). */
  const handleCopyFromGdn = (
    picked: { value: string; label: string; date_out: string | null } | null,
  ) => {
    if (!picked) return
    if (picked.value === (draft['gdn_id'] ?? '').trim()) {
      setCopyError('Cannot copy from the same GDN.')
      return
    }
    setCopyError('')
    void fetchQuality({ factoryCode, gdnId: picked.value })
      .unwrap()
      .then((data) => {
        const src = data as Record<string, FactoryCell>
        setDraft((prev) => {
          const next = { ...prev }
          for (const f of COPY_FIELDS) {
            next[f] = src[f] == null ? '' : formatStandard(src[f] as string | number)
          }
          return next
        })
      })
      .catch(() => setCopyError('Could not load source GDN quality.'))
  }

  /** BlackBroken + TriagesDefects auto-sum (Access Sub BlackBroken /
   *  TriagesDefects). */
  useEffect(() => {
    const black_and_broken = numOrZero(draft['blacks'] ?? '') + numOrZero(draft['brokens'] ?? '')
    let triages = 0
    for (const f of TRIAGES_INPUTS) {
      triages += f === 'black_and_broken' ? black_and_broken : numOrZero(draft[f] ?? '')
    }
    setDraft((prev) => {
      const nextBB = String(black_and_broken)
      const nextTr = String(triages)
      if (prev['black_and_broken'] === nextBB && prev['triages_defects'] === nextTr) return prev
      return { ...prev, black_and_broken: nextBB, triages_defects: nextTr }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    draft['blacks'], draft['brokens'], draft['spongy_beans'], draft['fm'],
    draft['mouldy'], draft['brown'], draft['bleach'], draft['partly_black'],
    draft['immature'], draft['broken_over_1_per_2'], draft['pulper_nipped_beans'],
    draft['blotchy_beans'], draft['waxy_beans'], draft['insect_beans'],
    draft['fb'], draft['overdry'], draft['yellowbeans'],
  ])

  // Required gate theo Access strCheck (isValid trước Update_Insert).
  const requiredMissing = HEADER_REQUIRED_FIELDS.some((f) => !(draft[f] ?? '').trim())
  const saveDisabled = isView || saving || requiredMissing

  const buildPayload = (): Record<string, FactoryCell | undefined> => {
    const payload: Record<string, FactoryCell | undefined> = {}
    for (const f of EDITABLE) {
      payload[f] = toPayloadValue(f, draft[f] ?? '')
    }
    // Computed (Access set in code, not user input).
    payload['black_and_broken'] = numOrZero(draft['black_and_broken'] ?? '')
    payload['triages_defects'] = numOrZero(draft['triages_defects'] ?? '')
    // note carried through (in strU but not in EDITABLE set).
    payload['note'] = toPayloadValue('note', draft['note'] ?? '')
    // Header block — persist vào bảng delivery (yêu cầu chị Đẹp).
    for (const f of HEADER_SAVE_FIELDS) {
      payload[f] = toPayloadValue(f, draft[f] ?? '')
    }
    return payload
  }

  const handleSave = () => {
    if (saveDisabled) return
    onSave(buildPayload())
  }

  // Access header caption: "<Factory> Factory - Delivery Quality" (btnQC mở
  // bound record editable, không phân biệt new/edit/view).
  const dialogTitle = useMemo(() => {
    if (mode === 'view') return 'Factory - Delivery Quality - View'
    return 'Factory - Delivery Quality'
  }, [mode])

  /** Header cell theo Access `Set_Enabled`: TextBox header → readonly display;
   *  ComboBox (do_no/lot_id/stack_id/package/package_to_truck) → editable;
   *  to_warehouse → chỉ enable sau do_no_AfterUpdate non-MOV. */
  const renderHeaderCell = (cell: { field: string; label: string }) => {
    if (!cell.field) return <Box key="empty" />
    const { field, label } = cell
    const raw = draft[field] ?? ''
    if (isView || HEADER_READONLY_FIELDS.has(field)) {
      const isNumber = NUMBER_DISPLAY_FIELDS.has(field)
      return (
        <TextField
          key={field}
          label={label}
          size="small"
          value={headerDisplayValue(field, raw)}
          disabled
          fullWidth
          sx={compactSx}
          InputProps={{ readOnly: true }}
          inputProps={isNumber ? { style: { textAlign: 'right' } } : undefined}
        />
      )
    }
    if (field === 'package' || field === 'package_to_truck') {
      return (
        <Autocomplete<string, false, false, false>
          key={field}
          size="small"
          options={PACKAGE_ENUM}
          value={raw || null}
          onChange={(_, v) => setDraft((prev) => ({ ...prev, [field]: v ?? '' }))}
          renderInput={(params) => (
            <TextField {...params} label={label} sx={compactSx} fullWidth />
          )}
        />
      )
    }
    if (field === 'do_no' || field === 'lot_id' || field === 'stack_id') {
      const items = field === 'do_no' ? doItems : field === 'lot_id' ? lotItems : stackItems
      const sel = field === 'do_no' ? selectedDo : field === 'lot_id' ? selectedLot : selectedStack
      return (
        <Autocomplete<ComboItem, false, false, false>
          key={field}
          size="small"
          options={items}
          value={sel}
          loading={combosQuery.isFetching}
          getOptionLabel={(o) => comboLabel(o.value, o.label)}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          onChange={(_, v) =>
            field === 'do_no'
              ? handleDoNoChange(v?.value ?? '')
              : setDraft((prev) => ({ ...prev, [field]: v?.value ?? '' }))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              required={field !== 'lot_id'}
              sx={compactSx}
              fullWidth
            />
          )}
        />
      )
    }
    // to_warehouse — TextBox duy nhất có cascade enable (do_no_AfterUpdate).
    return (
      <TextField
        key={field}
        label={label}
        size="small"
        fullWidth
        sx={compactSx}
        value={raw}
        disabled={!toWarehouseEnabled}
        onChange={(e) => setDraft((prev) => ({ ...prev, to_warehouse: e.target.value }))}
        InputProps={toWarehouseEnabled ? undefined : { readOnly: true }}
      />
    )
  }

  /** Render 1 defect cell — 2 cột: label | value. Computed cells readonly. */
  const renderGridCell = (field: string | null) => {
    if (!field) {
      return (
        <>
          <Box />
          <Box />
        </>
      )
    }
    const label = FIELD_LABELS[field] ?? field
    const value = draft[field] ?? ''
    const isComputed = COMPUTED_FIELDS.has(field)
    const isString = STRING_FIELDS.has(field)
    const editable = !isView && !isComputed && EDITABLE.has(field)
    // Access Form_Load `Set_Enabled` bật toàn bộ field quality (gồm cả
    // black_and_broken/triages_defects) → 2 field computed hiện ENABLED (không
    // xám), chỉ readOnly vì auto-sum (AfterUpdate). Header/note KHÔNG trong
    // Set_Enabled → disabled. View mode disable tất cả.
    // String: as-is. Editable number: raw while typing (blur → Standard).
    // Computed/readonly number: Standard format display (Access Format Standard).
    const display = isString || editable ? value : formatStandard(value)
    return (
      <>
        <Box sx={labelCellSx}>{label}</Box>
        <TextField
          size="small"
          type="text"
          value={display}
          onChange={(e) =>
            editable
              ? setDraft((prev) => ({ ...prev, [field]: e.target.value }))
              : undefined
          }
          onBlur={
            editable && !isString
              ? () =>
                  setDraft((prev) => {
                    const f = formatStandard(prev[field] ?? '')
                    return f === (prev[field] ?? '') ? prev : { ...prev, [field]: f }
                  })
              : undefined
          }
          disabled={isView || (!editable && !isComputed)}
          sx={gridInputSx}
          InputProps={isComputed ? { readOnly: true } : undefined}
          inputProps={isString ? undefined : { style: { textAlign: 'right' }, inputMode: 'decimal' }}
        />
      </>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          maxHeight: 'min(960px, 95vh)',
          width: 'min(1180px, 96vw)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          component="span"
          noWrap
          title={dialogTitle}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {dialogTitle}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {factoryCode.toUpperCase()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto' }}>
          {!isView && (
            <Button
              variant="contained"
              size="small"
              color="success"
              onClick={handleSave}
              disabled={saveDisabled}
              sx={titleBtnSx}
            >
              {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={saving}
            sx={titleBtnSx}
          >
            {isView ? 'Close' : 'Cancel'}
          </Button>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={{ pt: 1, overflowY: 'auto', flex: '1 1 auto' }}>
        {/* Delivery header (Enabled/Disabled theo Access Set_Enabled) — align
            theo HÀNG Access (col A 3180 / col B 9624); do_no riêng 1 hàng
            (col B trống). */}
        <Stack spacing={1}>
          {HEADER_ROWS.map((row, i) => (
            <Box
              key={i}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))' },
                columnGap: 1.5,
                rowGap: 1,
              }}
            >
              {row[0] ? renderHeaderCell(row[0]) : <Box />}
              {row[1] ? renderHeaderCell(row[1]) : <Box />}
            </Box>
          ))}
        </Stack>
        {/* Note — full width (Access Top 3012, Width 9564). TextBox ngoài
            Set_Enabled list → DISABLED (dù nằm trong strU save). */}
        <Box sx={{ mt: 1 }}>
          <TextField
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={draft['note'] ?? ''}
            disabled
            sx={compactSx}
            InputProps={{ readOnly: true }}
          />
        </Box>

        {/* Quality's Information — Access label (T3372) + gdn_id_copy combo
            (T3372, L6312) bên phải; style mirror "Copy Quality from another GRN"
            (FactoryReceiveQualityDialog): dropdown 3 cột id | dest | date_out. */}
        <Box
          sx={(theme) => ({
            px: 1.25,
            py: 0.5,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            borderLeft: 3,
            borderColor: 'primary.main',
            borderRadius: 0.5,
            mt: 1.5,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          })}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: 0.04 }}>
            Quality&apos;s Information
          </Typography>
          {!isView && (
            <Box sx={{ minWidth: 420, flex: '1 1 420px', maxWidth: 640, ml: 'auto' }}>
              <Autocomplete<(typeof gdnCopyOptions)[number], false, false, false>
                size="small"
                options={gdnCopyOptions}
                loading={combosQuery.isFetching}
                getOptionLabel={(o) => o.value ?? ''}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                filterOptions={(opts, state) => {
                  const needle = state.inputValue.trim().toUpperCase()
                  if (!needle) return opts.slice(0, 200)
                  return opts.filter((o) => {
                    const hay = `${o.label ?? ''} ${o.value}`.toUpperCase()
                    return hay.includes(needle)
                  }).slice(0, 200)
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.value}>
                    <Box sx={{ display: 'flex', gap: 1.5, width: '100%', fontSize: '0.78rem' }}>
                      <Box sx={{ flex: 1, fontWeight: 600 }}>{option.value}</Box>
                      <Box sx={{ flex: 1, color: 'text.secondary' }}>{option.label || '-'}</Box>
                      <Box sx={{ flex: 1, color: 'text.secondary' }}>
                        {formatDateDisplay(option.date_out ?? '')}
                      </Box>
                    </Box>
                  </li>
                )}
                onChange={(_, picked) => handleCopyFromGdn(picked)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Copy Quality from another GDN"
                    placeholder="Pick source GDN…"
                    sx={compactSx}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {copyLoading || combosQuery.isFetching ? (
                            <CircularProgress size={14} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {copyError ? (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: 'block', mt: 0.25, fontWeight: 600 }}
                >
                  {copyError}
                </Typography>
              ) : null}
            </Box>
          )}
        </Box>

        {/* Defect grid — 3 cột bám Access (Left 2352 / 7032 / 11040). */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' },
            columnGap: 1.5,
            rowGap: 0.5,
            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            borderRadius: 0.5,
            p: 0.75,
          }}
        >
          {QUALITY_COLUMNS.map((col, ci) => (
            <Box
              key={ci}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.3fr 1fr',
                columnGap: 0.75,
                rowGap: 0.5,
                alignItems: 'center',
                alignContent: 'start',
              }}
            >
              {col.map((f) => (
                <Box key={f} sx={{ display: 'contents' }}>
                  {renderGridCell(f)}
                </Box>
              ))}
            </Box>
          ))}
        </Box>

        {/* Bottom — Access: Deduction Rate (col1 T7680) | QC Name (col3 T7680) ;
            Supervisor (col1 T8004). 3-col grid auto-flow, col2 trống. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0,1fr))' },
            gap: 1,
            mt: 1.5,
          }}
        >
          <TextField
            label="Deduction Rate"
            size="small"
            type="text"
            value={draft['deduction_rate'] ?? ''}
            onChange={(e) => setDraft((prev) => ({ ...prev, deduction_rate: e.target.value }))}
            onBlur={() =>
              setDraft((prev) => {
                const f = formatStandard(prev['deduction_rate'] ?? '')
                return f === (prev['deduction_rate'] ?? '') ? prev : { ...prev, deduction_rate: f }
              })
            }
            disabled={isView}
            fullWidth
            sx={compactSx}
            inputProps={{ style: { textAlign: 'right' }, inputMode: 'decimal' }}
          />
          <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
          <TextField
            label="QC Name"
            size="small"
            value={draft['qc_name'] ?? ''}
            onChange={(e) => setDraft((prev) => ({ ...prev, qc_name: e.target.value }))}
            disabled={isView}
            fullWidth
            sx={compactSx}
          />
          <TextField
            label="Supervisor"
            size="small"
            value={draft['supervisor'] ?? ''}
            onChange={(e) => setDraft((prev) => ({ ...prev, supervisor: e.target.value }))}
            disabled={isView}
            fullWidth
            sx={compactSx}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
